import
  React
from 'react';

import
  styled
from 'styled-components';

import { Helmet } from 'react-helmet-async';

import ReactGA from 'react-ga4';

import {
  Environment,
  Theme
} from 'common';

import {
  Header,
  Loader,
  Text,
  Toast,
} from 'common/components';

import {
  useWidget,
} from 'common/contexts';

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  display: ${props => props.show ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  background-color: ${props => props.backgroundColor};
  flex: 1;
`;

const LoaderContainer = styled(Content)`
  display: ${props => props.show ? 'flex' : 'none'};
  background-color: ${Theme.Content.Background};
  justify-content: center;
  align-items: center;
  padding: 0 0 5vh 0;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 5;
`;

const ContentContent = styled.div`
  position: relative;
  width: 100%;
  ${props => props.applyMaxWidth && 'max-width: 1600px'};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const FooterBanner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${Theme.Footer.Background || '#1a1a1a'};
  color: ${Theme.Footer.Text || 'white'};
  padding: 8px 0;
  font-size: 12px;
  width: 100%;
`;

export const withCommon = (WrappedComponent, options = {}) => {

  const {
    applyMaxWidth = true,
    showHeader = true,
    showProfileMenu = false,
    transparentHeader = false,
    transitionHeader = false,
    showHeaderYourTickets = false,
    showLoadingOnLoad = true,
    contentBackgroundColor = 'var(--main-content-background-color)',
    addDefaultMetaTags = true,
  } = options;

  return props => {

    const [showLoader, setShowLoader] = React.useState({
      show: showLoadingOnLoad,
      message: ''
    });

    const [toast, setToast] = React.useState({
      show: false,
      message: ''
    });

    const isWidget = useWidget();

    const navigate = (path, options = {}) => {

      if (!path || !props.history) {
        return;
      }

      if (path === -1) {
        props.history.goBack();
      } else if (options.replace) {
        typeof props.history.replace === 'function' && props.history.replace(path);
      } else {
        typeof props.history.push === 'function' && props.history.push(path);
      }
    };

    const showLoading = (message) => {

      setShowLoader({
        show: true,
        message: message
      });
    };

    const hideLoading = () => {

      setShowLoader({
        show: false,
        message: ''
      });
    };

    const showToast = (message) => {

      setToast({
        show: true,
        message: message
      });
    };

    const hideToast = () => {

      setToast({
        show: false,
        message: ''
      });
    };

    const common = {
      pathParams: props.match?.params || {},
      history: props.history,
      location: props.location,
      isWidget,
      navigate,
      showLoading,
      hideLoading,
      showToast,
      hideToast,
    };

    React.useEffect(() => {

      if (Environment.name === 'Production') {
        const page = props.location.pathname + props.location.search;
        ReactGA.send({ hitType: "pageview", page: props.location.pathname + props.location.search, title: page });
      }
    }, [props.location]);

    return (

      <>
      {!isWidget && addDefaultMetaTags &&
        <Helmet>
          <title>WeTicket - Adventure awaits | Buy Tickets Online</title>
          <meta name="description" content="WeTicket is your trusted platform for buying tickets to events. Secure and easy online ticket booking." />
          
          {/* Default Open Graph tags */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://weticket.co.za/" />
          <meta property="og:title" content="WeTicket - Adventure awaits | Buy Tickets Online" />
          <meta property="og:description" content="Get your tickets to top events with WeTicket. Safe and secure online bookings." />
          <meta property="og:image" content="https://weticket.co.za/favicon.png" />
          
          {/* Default Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="WeTicket - Adventure awaits | Buy Tickets Online" />
          <meta name="twitter:description" content="Get your tickets to top events with WeTicket. Safe and secure online bookings." />
          <meta name="twitter:image" content="https://weticket.co.za/favicon.png" />
        </Helmet>
      }

      <Container>

        { showHeader && !isWidget &&

          <Header
            showHeaderYourTickets={showHeaderYourTickets}
            transitionHeader={transitionHeader}
            onLogoClick={() => navigate('/home')}
            navigate={navigate}
            showProfileMenu={showProfileMenu}
            isBackgroundTransparent={transparentHeader}
          />
        }

        <LoaderContainer show={showLoader.show}>

          <Loader/>

          { showLoader.message && 
          
            <Text zIndex={1} padding={'20px 0 0 0'}>{showLoader.message}</Text>
          }

        </LoaderContainer>
    
        <Content
          id={'main-content'}
          show={!showLoader.show}
          backgroundColor={contentBackgroundColor}>

          <ContentContent
            applyMaxWidth={applyMaxWidth}>

            <WrappedComponent
              common={common}
            />

          </ContentContent>

        </Content>

        {false && isWidget && (
          <FooterBanner>
            Powered By <a href="https://weticket.co.za" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '5px', color: Theme.Footer.Link || '#3498db', textDecoration: 'none' }}>Weticket</a>
          </FooterBanner>
        )}

        <Toast
          show={toast.show}
          message={toast.message}
          onClose={hideToast}
        />

      </Container>

      </>
    );
  };
};